/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Wed Sep 04 2024
 **/
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { css } from "@emotion/react";
import { v4 } from "uuid";
import { Checkbox, FormControlLabel, SvgIcon } from "@mui/material";
import { DatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { Button, Column, Row, TextField, Typography, useModal } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { Icon } from "@/frameworks/designStuff/components";
import { hoverScrollCss } from "@/frameworks/designStuff/utils";
import { Maybe } from "@/frameworks/libraries/components";
import { MessageList } from "@/frameworks/core/chat";

import * as S from "./private/emotion";
import { colors } from "@/frameworks/core/styles";
import { ModalID } from "@/frameworks/core/commons";
import { useChat } from "@/frameworks/core/API";
import { useConfirm } from "@/frameworks/core/hooks";

function MuiCustomCalendarIcon() {
	return (
		<SvgIcon>
			<Icon name="CalendarDaily24" />
		</SvgIcon>
	);
}

function hasDuplicateValues(items: { id: string; value: string }[]): boolean {
	const valueSet = new Set<string>();

	const allEmpty = items.every((item) => item.value === "");

	if (allEmpty) {
		return false;
	}

	for (const item of items) {
		if (valueSet.has(item.value)) {
			return true;
		}
		if (item.value) {
			valueSet.add(item.value);
		}
	}

	return false;
}

function hasEmptyValue(items: { id: string; value: string }[]): boolean {
	return !!items.some((item) => !item.value.trim());
}

/**
 * Function check the deadline of the vote. If the deadline is less than 15 minutes, it returns invalid.
 * @param timeStamp
 */
function hasTerminatedVoteAtLeastFifteenMinutes(timeStamp: number): boolean {
	const currentTime = dayjs().valueOf();
	const fifteenMinutesInMillis = 15 * 60 * 1000;
	return timeStamp - currentTime <= fifteenMinutesInMillis;
}

type VoteItem = { id: string; value: string };

export default function ChatVote({
	roomID,
	voteID,
	isUpdate = false
}: {
	roomID: string;
	voteID?: string;
	isUpdate?: boolean;
}) {
	const titleRef = useRef<HTMLInputElement>(null);
	const modal = useModal();
	const chatAPI = useChat();
	const { confirm } = useConfirm();

	const [voteTitle, setVoteTitle] = useState("");
	const [voteItems, setVoteItems] = useState<VoteItem[]>([
		{ id: v4(), value: "" },
		{ id: v4(), value: "" }
	]);

	const [voteCheckList, setVoteCheckList] = useState<string[]>([]);

	const [isError, setIsError] = useState({
		isEmptyTitle: false,
		hasNotMultipleEntries: false,
		hasDuplicate: false,
		hasEmpty: false,
		hasTerminated: false,
		isEmptyEndDate: false,
		isEmptyEndTime: false
	});

	const [formData, setFormData] = useState({
		startDate: dayjs().add(2, "day").format("YYYY-MM-DDTHH:mm:ss"),
		timestamp: dayjs().add(2, "day").valueOf()
	});

	const { data: updateData } = useQuery({
		queryKey: ["vote:detail", voteID],
		queryFn: async () => {
			if (!isUpdate) return null;
			return (await chatAPI.vote.detail.execute({ roomId: roomID, voteId: String(voteID) }))
				.data as MessageList[number]["vote"];
		}
	});

	useEffect(() => {
		if (updateData && isUpdate) {
			setVoteTitle(updateData.title);
			setVoteItems(
				updateData.items.map((item) => ({
					id: item.voteItemId,
					value: item.content
				}))
			);
		}

		if (!isUpdate) {
			if (!titleRef.current) return;
			titleRef?.current?.focus();
		}
	}, [updateData, isUpdate]);

	const handleVoteItem = () => {
		setVoteItems((prev) => {
			if (!prev) return prev;

			if (prev.length < 10) {
				return [...prev, { id: v4(), value: "" }];
			}

			return prev;
		});
	};

	const removeVoteItem = (id: string) => {
		const newVoteItems = voteItems.filter((voteItem) => voteItem.id !== id);
		setVoteItems(newVoteItems);
		setIsError((prev) => ({
			...prev,
			hasNotMultipleEntries: !newVoteItems[0].value.length || !newVoteItems[0].value.length,
			hasDuplicate: hasDuplicateValues(newVoteItems),
			hasEmpty: hasEmptyValue(newVoteItems)
		}));
	};

	const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
		// TODO checkbox 선택시 이름에 맞게 값 저장
		setVoteCheckList((prev) => {
			if (prev.find((item) => item === e.target.name)) return prev.filter((item) => item !== e.target.name);
			else return prev.concat(e.target.name);
		});
	};

	const handleVoteChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>, id: string) => {
			const changeData = voteItems.map((item) => {
				if (item.id === id) {
					return {
						...item,
						value: e.currentTarget.value
					};
				}
				return item;
			});

			setVoteItems(changeData);
			setIsError((prev) => ({
				...prev,
				hasNotMultipleEntries: !changeData[0].value.length || !changeData[0].value.length,
				hasDuplicate: hasDuplicateValues(changeData),
				hasEmpty: hasEmptyValue(changeData)
			}));
		},
		[voteItems]
	);

	// TODO 유효성 검사 관련 로직 수정해야함. ( useForm 사용 )
	const validateChecker = useCallback(() => {
		if (!voteTitle.length) {
			setIsError((prev) => ({
				...prev,
				isEmptyTitle: Boolean(!voteTitle.length)
			}));
			return false;
		}
		if (!voteItems[0].value.length || !voteItems[1].value.length) {
			setIsError((prev) => ({
				...prev,
				hasNotMultipleEntries: !voteItems[0].value.length || !voteItems[1].value.length
			}));
			return false;
		}
		if (hasEmptyValue(voteItems)) {
			setIsError((prev) => ({
				...prev,
				hasEmpty: hasEmptyValue(voteItems)
			}));
			return false;
		}
		if (hasDuplicateValues(voteItems)) {
			setIsError((prev) => ({
				...prev,
				hasDuplicate: hasDuplicateValues(voteItems)
			}));
			return false;
		}
		if (hasTerminatedVoteAtLeastFifteenMinutes(formData.timestamp)) {
			setIsError((prev) => ({
				...prev,
				hasTerminated: hasTerminatedVoteAtLeastFifteenMinutes(formData.timestamp)
			}));
			return false;
		}

		return true;
	}, [voteTitle, voteItems, formData]);

	const handleCreateVote = useCallback(async () => {
		if (!validateChecker()) return;

		confirm(_T("투표를 생성하시겠습니까?"), {
			async onConfirm() {
				// TODO useForm으로 에러처리 변경
				await chatAPI.vote.create.execute({
					title: voteTitle,
					deadline: formData.timestamp,
					roomId: roomID,
					items: voteItems.map((item) => ({
						content: item.value
					})),
					options: {
						allowMultipleChoice: Boolean(voteCheckList.find((option) => option === "allowMultipleChoice")),
						allowAnonymous: Boolean(voteCheckList.find((option) => option === "allowAnonymous")),
						notifyBeforeEnd: Boolean(voteCheckList.find((option) => option === "notifyBeforeEnd"))
					}
				});
				closeModal();
			}
		});
	}, [formData, voteTitle, voteCheckList, voteItems]);

	const handleEndVote = () => {
		if (!voteID) return;
		confirm(_T("투표를 종료하시겠습니까?"), {
			async onConfirm() {
				await chatAPI.vote.end.execute({
					voteId: voteID,
					roomId: roomID
				});

				closeModal();
			}
		});
	};

	const handleUpdateVote = async () => {
		// Update 07/01/2025: Add logic validation to function update
		if (!validateChecker()) return;
		if (!voteID) return;
		await chatAPI.vote.update.execute({
			voteId: voteID,
			title: voteTitle,
			deadline: formData.timestamp,
			roomId: roomID,
			items: voteItems.map((item) => ({
				content: item.value
			})),
			options: {
				allowMultipleChoice: Boolean(updateData?.allowAnonymous),
				allowAnonymous: Boolean(updateData?.allowAnonymous),
				notifyBeforeEnd: Boolean(updateData?.notifyBeforeEnd)
			}
		});
		closeModal();
	};

	const closeModal = () => {
		modal.hide(ModalID.CHAT_VOTE);
	};

	/**
	 * Handle validate deadline of vote when change the date, time picker
	 */
	const handleValidateDeadlineOfVote = useCallback((deadlineTimeStamp: number) => {
		setIsError((prev) => ({
			...prev,
			hasTerminated: hasTerminatedVoteAtLeastFifteenMinutes(deadlineTimeStamp)
		}));
	}, []);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
			<S.ChatVoteWrapper>
				<S.ContentWrapper css={[hoverScrollCss()]}>
					<Typography.div typoSize="Head6" style={{ marginBottom: "20px" }}>
						{_T("투표 만들기")}
					</Typography.div>
					<Column gap={16} style={{ width: "100%" }}>
						<S.VoteTitle gap={12}>
							<Row horizonAlign="distribute" style={{ width: "100%" }}>
								<Row gap={3}>
									<Typography typoSize="Body4" typoColor="neutral_60_100">
										{_T("투표 제목")}
									</Typography>
									<Typography typoSize="Body4" typoColor="error_50_100">
										{_T("(필수)")}
									</Typography>
								</Row>
								<Typography typoSize="Body4" typoColor="neutral_80_100">
									{`${voteTitle ? voteTitle?.length : 0} / 30`}
								</Typography>
							</Row>

							<TextField.Icon
								ref={titleRef}
								placeholder={_T("투표 제목을 입력하세요")}
								value={voteTitle}
								onChange={(e) => {
									setVoteTitle(e.target.value);
									setIsError((prev) => ({
										...prev,
										isEmptyTitle: Boolean(!e.target.value.trim().length)
									}));
								}}
								maxLength={30}
								readOnly={isUpdate}
								error={isError.isEmptyTitle}
								help={isError.isEmptyTitle ? _T("제목을 입력해주세요") : ""}
							/>
						</S.VoteTitle>
						{/* 투표항목 */}
						<S.VoteItem>
							<S.VoteItem gap={8}>
								{voteItems.map((item, index: number) => (
									<Row
										key={item.id}
										verticalAlign="center"
										gap={8}
										style={{ width: "100%", marginTop: index === 0 ? "8px" : 0 }}
									>
										<TextField
											placeholder="투표 항목을 입력하세요"
											defaultValue={voteItems.find((vote) => vote.id === item.id)?.value}
											onChange={(e) => {
												handleVoteChange(e, item.id);
											}}
											maxLength={20}
											readOnly={isUpdate}
										/>
										<Maybe test={index > 1}>
											<Icon name="Delete24" onClick={() => removeVoteItem(item.id)} />
										</Maybe>
									</Row>
								))}
								<Maybe test={isError.hasNotMultipleEntries}>
									<Typography
										typoColor="error_50_100"
										typoSize="Body5"
										style={{ cursor: "default", marginTop: "4px" }}
									>
										{_T("투표 항목을 2가지 이상 입력해주세요")}
									</Typography>
								</Maybe>
								<Maybe test={isError.hasEmpty}>
									<Typography
										typoColor="error_50_100"
										typoSize="Body5"
										style={{ cursor: "default", marginTop: "4px" }}
									>
										{_T("입력되지 않은 항목이 있습니다.")}
									</Typography>
								</Maybe>
								<Maybe test={isError.hasDuplicate}>
									<Typography
										typoColor="error_50_100"
										typoSize="Body5"
										style={{ cursor: "default", marginTop: "4px" }}
									>
										{_T("중복된 항목이 존재합니다.")}
									</Typography>
								</Maybe>
							</S.VoteItem>
							<Maybe
								test={isUpdate}
								fallback={
									<Button
										style={{
											width: "100%",
											margin: "8px 0",
											backgroundColor: colors.secondary_80_12
										}}
										typoColor="neutral_80_100"
										onClick={handleVoteItem}
									>
										{_T("+ 항목 추가")}
									</Button>
								}
							/>
						</S.VoteItem>
						{/* 투표옵션 */}
						<S.VoteOption>
							<Typography typoSize="Body4" typoColor="neutral_80_100">
								{_T("투표 옵션")}
							</Typography>
							<S.VoteItemWrapper>
								<S.LabelWrapper>
									<FormControlLabel
										control={
											<Checkbox
												name="allowMultipleChoice"
												checked={
													isUpdate
														? Boolean(updateData?.allowMultipleChoice)
														: Boolean(
																voteCheckList.find(
																	(item) => item === "allowMultipleChoice"
																)
															)
												}
												onChange={handleCheckbox}
												disabled={isUpdate}
											/>
										}
										label={
											<Typography typoSize="Body3" typoColor="neutral_80_100">
												{_T("복수선택")}
											</Typography>
										}
									/>
								</S.LabelWrapper>
								<S.LabelWrapper>
									<FormControlLabel
										style={{ marginRight: 0 }}
										control={
											<Checkbox
												name="allowAnonymous"
												checked={
													isUpdate
														? Boolean(updateData?.allowAnonymous)
														: Boolean(
																voteCheckList.find((item) => item === "allowAnonymous")
															)
												}
												onChange={handleCheckbox}
												disabled={isUpdate}
											/>
										}
										label={
											<Typography typoSize="Body3" typoColor="neutral_80_100">
												{_T("익명투표")}
											</Typography>
										}
									/>
								</S.LabelWrapper>
								<S.LabelWrapper>
									<FormControlLabel
										style={{ marginRight: 0 }}
										control={
											<Checkbox
												name="notifyBeforeEnd"
												checked={
													isUpdate
														? Boolean(updateData?.notifyBeforeEnd)
														: Boolean(
																voteCheckList.find((item) => item === "notifyBeforeEnd")
															)
												}
												onChange={handleCheckbox}
												disabled={isUpdate}
											/>
										}
										label={
											<Typography typoSize="Body3" typoColor="neutral_80_100">
												{_T("투표 종료 30분전 알림")}
											</Typography>
										}
									/>
								</S.LabelWrapper>
							</S.VoteItemWrapper>
						</S.VoteOption>
						{/* 투표종료시간 */}
						<Column gap={8}>
							<Typography typoSize="Body4" typoColor="neutral_80_100">
								{_T("투표 종료 시간")}
							</Typography>
							<Row gap={4}>
								<DatePicker
									css={DatePickerStyle}
									disablePast={true}
									format="YYYY.MM.DD"
									showDaysOutsideCurrentMonth
									value={dayjs(formData?.startDate)}
									slots={{
										openPickerIcon: MuiCustomCalendarIcon
									}}
									slotProps={{
										popper: {
											disablePortal: true
										}
									}}
									onChange={(value) => {
										const originalDateTimeObj = dayjs(formData.startDate, "YYYY-MM-DD HH:mm:ss");
										const newDateTimeObj = originalDateTimeObj
											.set("date", dayjs(value as Dayjs).date())
											.set("month", dayjs(value as Dayjs).month())
											.set("year", dayjs(value as Dayjs).year());

										const isEmptyDate = !dayjs(value).isValid();

										setFormData((prev) => ({
											...prev,
											startDate: newDateTimeObj.format("YYYY-MM-DDTHH:mm:ss")
										}));

										setIsError((prev) => ({
											...prev,
											isEmptyEndDate: isEmptyDate
										}));

										handleValidateDeadlineOfVote(newDateTimeObj.valueOf());
									}}
								/>
								<TimePicker
									css={TimePickerStyle}
									disablePast={true}
									closeOnSelect={false}
									format="HH:mm"
									value={dayjs()
										.hour(dayjs(formData.startDate).get("hour"))
										.minute(dayjs(formData.startDate).get("minute"))}
									timeSteps={{ minutes: 1 }}
									onChange={(value) => {
										const originalDateTimeObj = dayjs(formData.startDate, "YYYY-MM-DD HH:mm:ss");
										const newDateTimeObj = originalDateTimeObj
											.set("hour", dayjs(value as Dayjs, "HH:mm:ss").hour())
											.set("minute", dayjs(value as Dayjs, "HH:mm:ss").minute())
											.set("second", dayjs(value as Dayjs, "HH:mm:ss").second());

										const isEmptyTime = !dayjs(value).isValid();

										setIsError((prev) => ({
											...prev,
											isEmptyEndTime: isEmptyTime
										}));

										handleValidateDeadlineOfVote(newDateTimeObj.valueOf());

										setFormData((prev) => ({
											...prev,
											startDate: newDateTimeObj.format("YYYY-MM-DDTHH:mm:ss"),
											timestamp: newDateTimeObj.valueOf()
										}));
									}}
									slotProps={{
										popper: {
											disablePortal: true
										}
									}}
								/>
							</Row>
							<Maybe test={isError.hasTerminated}>
								<Typography
									typoColor="error_50_100"
									typoSize="Body5"
									style={{ cursor: "default", marginTop: "4px" }}
								>
									{_T("현재 시간으로부터 최소 15분 뒤에 종료할 수 있습니다.")}
								</Typography>
							</Maybe>
						</Column>
					</Column>
				</S.ContentWrapper>
				<S.BottomBtn verticalAlign="center" horizonAlign="center">
					<Button.Group>
						<S.FooterButton size="large" filled="secondary" onClick={closeModal}>
							{_T("취소")}
						</S.FooterButton>
						<Maybe
							test={isUpdate}
							fallback={
								<S.FooterButton
									size="medium"
									onClick={handleCreateVote}
									disabled={Object.values(isError).some((value) => value === true)}
								>
									{_T("투표 생성")}
								</S.FooterButton>
							}
						>
							<S.FooterButton size="medium" onClick={handleEndVote}>
								{_T("투표 종료")}
							</S.FooterButton>
							<S.FooterButton size="medium" onClick={handleUpdateVote}>
								{_T("투표 수정")}
							</S.FooterButton>
						</Maybe>
					</Button.Group>
				</S.BottomBtn>
			</S.ChatVoteWrapper>
		</LocalizationProvider>
	);
}

const TimePickerStyle = css`
	> div {
		width: 182px;
		height: 40px;
		color: #242424;
		border: 1px solid #cdd0d6;
		border-radius: 8px;
		flex-direction: row-reverse;

		fieldset {
			border: none;
		}
	}
`;

const DatePickerStyle = css`
	> div {
		width: 182px;
		height: 40px;
		color: #242424;
		border: 1px solid #cdd0d6;
		border-radius: 8px;
		flex-direction: row-reverse;

		fieldset {
			border: none;
		}
	}
`;
